import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { stopSTT } from './sttClient';
import { LanguageProvider } from './contexts/LanguageContext';
import Home from './components/Home';
import HomeOld from './components/HomeOld';
import BrainWarmup from './components/BrainWarmup';
import PostAssessment from './components/PostAssessment';
import AdminUpload from './components/AdminUpload';
import AdminChaptersUpload from './components/AdminChaptersUpload';
import AdminPracticeUpload from './components/AdminPracticeUpload';
import ExploreLearning from './components/ExploreLearning';
import Learn from './components/Learn';
import SpeakingPage from './components/SpeakingPage';
import ReadingPage from './components/ReadingPage';
import WritingPage from './components/WritingPage';
import ListeningPage from './components/ListeningPage';
import BusinessScenario from './components/BusinessScenario';
import CulturalInsights from './components/CulturalInsights';
import CulturalInsightsChat from './components/CulturalInsightsChat';
import CulturalInsightsChallenge from './components/CulturalInsightsChallenge';
import CulturalInsightsChallengeQuiz from './components/CulturalInsightsChallengeQuiz';
import CIChatHistory from './components/CIChatHistory';
import BusinessConversation from './components/BusinessConversation';
import ConversationSummary from './components/ConversationSummary';
import LogoutPage from './components/LogoutPage';
import ChatHistory from './components/ChatHistory';
import AdminReport from './components/AdminReport';
import ExamPrep from './components/ExamPrep';
import Dashboard from './components/Dashboard';
import AssessmentList from './components/AssessmentList';
import ExamPrepList from './components/ExamPrepList';
import SessionTimeoutModal from './components/SessionTimeoutModal';
import useSessionTimeout from './hooks/useSessionTimeout';
import './App.css';

// True while the user is intentionally logging out, so the app never bounces them
// to /auth/login (which SSO would silently re-auth). Also true on the /logout route.
const isLoggingOut = () => window.__lbLoggingOut === true || window.location.pathname === '/logout';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // Refetch user levels when navigating to home
  useEffect(() => {
    if (location.pathname === '/' && user && !loading) { fetchUserLevels(); }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionModalType, setSessionModalType] = useState(null); // 'warning' or 'expired'
  const [userLevels, setUserLevels] = useState(null); // null = not loaded yet
  
  // Check if running locally
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Fetch user levels globally - called on mount and when navigating
  const fetchUserLevels = () => {
    axios.get('/lb-api/pre-assessment/user-levels?t=' + Date.now(), { withCredentials: true })
      .then(res => {
        if (res.data.success && res.data.levels) {
          const map = {};
          res.data.levels.forEach(l => { map[l.language_code] = l.assigned_level; });
          setUserLevels(map);
          Object.keys(map).forEach(lang => localStorage.setItem('assessmentCompleted_' + lang, 'true'));
        } else {
          setUserLevels({});
        }
      }).catch(() => setUserLevels({}));
  };

  // Session timeout handlers
  const handleSessionWarning = () => {
    // Flag so the axios interceptor won't hard-redirect while the modal is up.
    window.__lbSessionModal = true;
    setSessionModalType('warning');
    // Stop any active microphone recording
    stopSTT();
    // Pause activity tracking when warning modal appears
    pauseTimeout();
    
    // Set a 2-minute timer for auto-expiration
    setTimeout(() => {
      // Only expire if still showing warning (user didn't click Continue)
      setSessionModalType(current => {
        if (current === 'warning') {
          handleSessionExpired();
          return 'expired';
        }
        return current;
      });
    }, 1 * 60 * 1000); // 1 minute
  };

  const handleSessionExpired = () => {
    // Keep the interceptor from redirecting away while the expired popup is shown.
    window.__lbSessionModal = true;
    // Show the expired popup on the SAME page. Do NOT clear the user or call
    // logout here — clearing the user re-renders routes/guards and navigates the
    // page away (to home) before the popup is useful. The actual logout happens
    // when the user clicks "Login" in the modal (navigates to /auth/login).
    setSessionModalType('expired');
    // Stop any active microphone recording.
    stopSTT();
  };

  const handleContinueSession = () => {
    window.__lbSessionModal = false;
    setSessionModalType(null);
    // Resume activity tracking and reset timers
    resumeTimeout();
  };

  const handleLogout = () => {
    setSessionModalType(null);
    setUser(null);
    if (!isLocal) {
      axios.post('/auth/logout', {}, { withCredentials: true }).catch(err => {
        console.error('Logout error:', err);
      });
    }
    navigate('/logout');
  };

  const handleLoginAgain = () => {
    setSessionModalType(null);
    navigate('/logout');
  };

  // Initialize session timeout (only for production)
  const { resetTimers, pauseTimeout, resumeTimeout } = useSessionTimeout(
    !isLocal ? handleSessionWarning : null,
    !isLocal ? handleSessionExpired : null
  );

  // Axios interceptor to detect session expiry from API errors
  useEffect(() => {
    if (isLocal) return;
    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const url = error.config?.url || '';
        // Skip session expired handling while logging out, and for logout page,
        // debug/token, disclaimer, and auth endpoints.
        if (isLoggingOut() || url.includes('/debug/token') || url.includes('/disclaimer/') || url.includes('/auth/') || url.includes('/ci-challenge/')) {
          return Promise.reject(error);
        }
        const msg = String(error.response?.data?.error || error.response?.data?.message || '');
        if (error.response?.status === 401 || msg.includes('not authenticated') || msg.includes('Session unavailable')) {
          // If the session-timeout modal is showing, let it handle the flow —
          // don't hard-redirect to login (that would navigate away from the
          // page and hide the expired popup).
          if (window.__lbSessionModal === true) {
            return Promise.reject(error);
          }
          // Stop any active microphone recording before redirecting
          stopSTT();
          // Redirect to login
          window.location.href = "/auth/login?returnTo=" + encodeURIComponent(window.location.pathname);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [isLocal]);

  useEffect(() => {
    if (isLocal) {
      // Local development: Auto-login with dummy user
      setUser({ 
        name: 'Local Dev User', 
        email: 'dev@localhost.com', 
        id: 'local-dev-user' 
      });
      fetchUserLevels();
      setLoading(false);
    } else {
      // Production (Ubuntu): Check SSO authentication
      const checkAuth = (retryCount = 0) => {
        axios.get("/auth/me", { withCredentials: true })
          .then(res => {
            setUser(res.data);
            fetchUserLevels();
            setLoading(false);
          })
          .catch(() => {
            // Don't bounce to SSO login while the user is on the logout page —
            // that would silently re-authenticate them right after logging out.
            if (isLoggingOut()) {
              setLoading(false);
              return;
            }
            // Retry once after a short delay (handles race condition after OAuth redirect)
            if (retryCount < 1) {
              setTimeout(() => checkAuth(retryCount + 1), 1000);
            } else {
              // Not authenticated, redirect to SSO login with return URL
              window.location.href = "/auth/login?returnTo=" + encodeURIComponent(window.location.pathname + window.location.search);
            }
          });
      };
      checkAuth();
    }
  }, [isLocal]);

  if (loading) return <div>Loading...</div>;

  // Guard component: redirects to home if pre-assessment not completed for any language
  const AssessmentGuard = ({ children }) => {
    const languages = ['en', 'fr', 'de', 'es', 'it', 'ja'];
    const anyCompleted = languages.some(lang => localStorage.getItem('assessmentCompleted_' + lang) === 'true');
    if (!anyCompleted) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  // Admin guard: checks if user's enterprise_id is in admin_users table
  const AdminGuard = ({ children }) => {
    const [isAdmin, setIsAdmin] = useState(null);

    useEffect(() => {
      const checkAdmin = async () => {
        try {
          const res = await axios.get('/lb-api/admin/check', { withCredentials: true });
          setIsAdmin(res.data.isAdmin);
        } catch {
          setIsAdmin(false);
        }
      };
      checkAdmin();
    }, []);

    if (isAdmin === null) return <div>Loading...</div>;
    if (!isAdmin) return <Navigate to="/" replace />;
    return children;
  };

  return (
    <LanguageProvider>
      <Routes>
        <Route path="/" element={<Home user={user} key={location.key} />} />
        <Route path="/home-old" element={<HomeOld user={user} />} />
        <Route path="/brain-warmup" element={<BrainWarmup user={user} />} />
        <Route path="/post-assessment" element={<PostAssessment user={user} />} />
        <Route path="/admin/upload" element={<AdminGuard><AdminUpload user={user} /></AdminGuard>} />
        <Route path="/admin/chapters" element={<AdminGuard><AdminChaptersUpload user={user} /></AdminGuard>} />
        <Route path="/admin/practice" element={<AdminGuard><AdminPracticeUpload user={user} /></AdminGuard>} />
        <Route path="/explore-learning" element={<AssessmentGuard><ExploreLearning user={user} /></AssessmentGuard>} />
        <Route path="/learn" element={<AssessmentGuard><Learn user={user} /></AssessmentGuard>} />
        <Route path="/practice-speaking" element={<AssessmentGuard><SpeakingPage user={user} /></AssessmentGuard>} />
        <Route path="/practice-reading" element={<AssessmentGuard><ReadingPage user={user} /></AssessmentGuard>} />
        <Route path="/practice-writing" element={<AssessmentGuard><WritingPage user={user} /></AssessmentGuard>} />
        <Route path="/practice-listening" element={<AssessmentGuard><ListeningPage user={user} /></AssessmentGuard>} />
        <Route path="/practice-business" element={<AssessmentGuard><BusinessScenario user={user} /></AssessmentGuard>} />
        <Route path="/cultural-insights" element={<AssessmentGuard><CulturalInsights user={user} /></AssessmentGuard>} />
        <Route path="/cultural-insights-chat" element={<AssessmentGuard><CulturalInsightsChat user={user} /></AssessmentGuard>} />
        <Route path="/cultural-insights-challenge" element={<AssessmentGuard><CulturalInsightsChallenge user={user} /></AssessmentGuard>} />
        <Route path="/cultural-insights-challenge-quiz" element={<AssessmentGuard><CulturalInsightsChallengeQuiz user={user} /></AssessmentGuard>} />
        <Route path="/ci-chat-history" element={<AssessmentGuard><CIChatHistory user={user} /></AssessmentGuard>} />
        <Route path="/business-conversation" element={<AssessmentGuard><BusinessConversation user={user} /></AssessmentGuard>} />
        <Route path="/conversation-summary" element={<AssessmentGuard><ConversationSummary user={user} /></AssessmentGuard>} />
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/chat-history" element={<AssessmentGuard><ChatHistory user={user} /></AssessmentGuard>} />
        <Route path="/exam-prep" element={<AssessmentGuard><ExamPrep user={user} /></AssessmentGuard>} />
        <Route path="/dashboard" element={<Dashboard user={user} />} />
        <Route path="/assessment-list" element={<AssessmentList user={user} />} />
        <Route path="/exam-prep-list" element={<ExamPrepList user={user} />} />
        <Route path="/admin-report" element={<AdminGuard><AdminReport user={user} /></AdminGuard>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Session Timeout Modal */}
      {sessionModalType && (
        <SessionTimeoutModal
          type={sessionModalType}
          onContinue={handleContinueSession}
          onLogout={handleLogout}
          onLogin={handleLoginAgain}
        />
      )}
    </LanguageProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
